// Configuration ids: a documented mixed-radix scheme, so that a configuration can be reconstructed from its id.
//
// Base order: attributes in the order of the CSV header, categories of an attribute in the order of their
// first appearance in the rows. A permutation is ranked lexicographically with respect to the base order
// (Lehmer code). With attribute order a_1 ... a_n and category orders c_1 ... c_n (c_i belongs to a_i):
//
//   id = (...((rank(a) * |C_a1|! + rank(c_1)) * |C_a2|! + rank(c_2)) ...) * |C_an|! + rank(c_n)
//
// Consequences: all configurations of one attribute order are contiguous, and within it all configurations
// of one category order of the first attribute are contiguous. Ids are below 2^53 for every dataset here.

const FACTORIALS = [1];
export function factorial(n) {
    for (let i = FACTORIALS.length; i <= n; i++) FACTORIALS.push(FACTORIALS[i - 1] * i);
    return FACTORIALS[n];
}

// Rank of a permutation given as indices into the base order.
export function permutationRank(indices) {
    const n = indices.length;
    let rank = 0;
    for (let i = 0; i < n; i++) {
        let smaller = 0;
        for (let j = i + 1; j < n; j++) if (indices[j] < indices[i]) smaller++;
        rank += smaller * factorial(n - 1 - i);
    }
    return rank;
}

export function permutationUnrank(rank, n) {
    const pool = Array.from({ length: n }, (_, i) => i);
    const indices = [];
    for (let i = 0; i < n; i++) {
        const f = factorial(n - 1 - i);
        const digit = Math.floor(rank / f);
        rank -= digit * f;
        indices.push(pool.splice(digit, 1)[0]);
    }
    return indices;
}

/**
 * Base order of a dataset from dataset-loader.js.
 * @returns [{ name, categories: [string] }]
 */
export function baseOrder(dataset) {
    return dataset.header.map(name => {
        const categories = [];
        const seen = new Set();
        for (const row of dataset.rows) {
            if (!seen.has(row[name])) {
                seen.add(row[name]);
                categories.push(row[name]);
            }
        }
        return { name, categories };
    });
}

// Number of configurations: |D|! * prod |C_d|!
export function configCount(base) {
    return base.reduce((acc, a) => acc * factorial(a.categories.length), factorial(base.length));
}

// Number of configurations per attribute order.
export function configsPerAttributeOrder(base) {
    return base.reduce((acc, a) => acc * factorial(a.categories.length), 1);
}

export function idToConfig(id, base) {
    const perOrder = configsPerAttributeOrder(base);
    const attributeRank = Math.floor(id / perOrder);
    let rest = id - attributeRank * perOrder;
    const attributes = permutationUnrank(attributeRank, base.length).map(i => base[i]);
    let block = perOrder;
    return attributes.map(a => {
        block /= factorial(a.categories.length);
        const rank = Math.floor(rest / block);
        rest -= rank * block;
        return { name: a.name, categories: permutationUnrank(rank, a.categories.length).map(i => a.categories[i]) };
    });
}

// Throws if the configuration is not a permutation of the base order.
export function configToId(config, base) {
    if (config.length !== base.length) throw new Error('Configuration has the wrong number of attributes.');
    const attributeIndices = config.map(c => {
        const index = base.findIndex(a => a.name === c.name);
        if (index < 0) throw new Error('Unknown attribute: ' + c.name);
        return index;
    });
    if (new Set(attributeIndices).size !== base.length) throw new Error('Duplicate attribute in configuration.');
    let id = permutationRank(attributeIndices);
    config.forEach((c, k) => {
        const categories = base[attributeIndices[k]].categories;
        if (c.categories.length !== categories.length) throw new Error('Wrong number of categories: ' + c.name);
        const indices = c.categories.map(name => {
            const index = categories.indexOf(typeof name === 'string' ? name : name.name);
            if (index < 0) throw new Error('Unknown category of ' + c.name + ': ' + name);
            return index;
        });
        if (new Set(indices).size !== categories.length) throw new Error('Duplicate category in ' + c.name);
        id = id * factorial(categories.length) + permutationRank(indices);
    });
    return id;
}

// Rank of the attribute order of a configuration id.
export function attributeOrderRank(id, base) {
    return Math.floor(id / configsPerAttributeOrder(base));
}

// Id of the mirrored configuration (every category order reversed).
export function mirrorId(id, base) {
    return configToId(idToConfig(id, base).map(a => ({ name: a.name, categories: a.categories.slice().reverse() })), base);
}
